import {
  ClipboardList, ChefHat 
} from 'lucide-react';
import { cn } from '@/lib/classnames';
import { Button } from '@/components/ui/button';
import type { PurchaseOrder } from '../../types';

interface PrepListTabProps {readonly po: PurchaseOrder;}

function getStorageLocation(itemName: string): string {
  const name = itemName.toLowerCase();
  if (
    name.includes('salmon') ||
    name.includes('shrimp') ||
    name.includes('fish')
  ) {
    return 'Walk-in Freezer A';
  }
  if (
    name.includes('cream') ||
    name.includes('butter') ||
    name.includes('cheese')
  ) {
    return 'Dairy Cooler';
  }
  if (
    name.includes('tomato') ||
    name.includes('lettuce') ||
    name.includes('produce')
  ) {
    return 'Produce Cooler';
  }
  if (
    name.includes('ribeye') ||
    name.includes('chicken') ||
    name.includes('meat')
  ) {
    return 'Meat Cooler';
  }
  if (
    name.includes('vodka') ||
    name.includes('wine') ||
    name.includes('beer')
  ) {
    return 'Beverage Storage';
  }
  return 'Dry Storage';
}

function getPrepInstructions(itemName: string): string {
  const name = itemName.toLowerCase();
  if (name.includes('salmon') || name.includes('fish')) {
    return 'Store at -18°C, thaw 24h before use';
  }
  if (name.includes('shrimp')) {
    return 'Keep frozen, thaw in cold water before service';
  }
  if (name.includes('cream') || name.includes('butter')) {
    return 'Refrigerate immediately at 2-4°C';
  }
  if (name.includes('tomato')) {
    return 'Store at room temp, refrigerate when ripe';
  }
  if (name.includes('ribeye') || name.includes('chicken')) {
    return 'Refrigerate at 0-2°C, use within 3 days';
  }
  return 'Store per label instructions';
}

export function PrepListTab({ po }: PrepListTabProps) {
  const prepItems = po.lines.map((line) => ({
    id: line.lineId,
    itemName: line.itemName,
    orderedQty: line.quantity,
    unit: line.unit,
    storageLocation: getStorageLocation(line.itemName),
    prepInstructions: getPrepInstructions(line.itemName),
    assignedTo: 'Kitchen Team',
    status: po.status === 'delivered' ? 'ready' : 'pending',
  }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-gray-900">
            Prep List for {po.poNumber}
          </h3>
          <p className="text-sm text-gray-500">
            {prepItems.length} items to prepare upon delivery
          </p>
        </div>
        <Button variant="outline" size="sm">
          <ClipboardList className="mr-2 h-4 w-4" />
          Print Prep List
        </Button>
      </div>

      <div className="rounded-lg border">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                Item
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                Quantity
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                Storage
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                Prep Instructions
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {prepItems.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{item.itemName}</p>
                  <p className="text-xs text-gray-500">
                    Assigned: {item.assignedTo}
                  </p>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {item.orderedQty} {item.unit}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {item.storageLocation}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {item.prepInstructions}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={cn(
                      'inline-flex items-center rounded-full px-2 py-1 text-xs font-medium',
                      item.status === 'ready'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    )}
                  >
                    {item.status === 'ready' ? 'Ready' : 'Pending Delivery'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-lg border bg-amber-50 p-4">
        <div className="flex items-start gap-3">
          <ChefHat className="h-5 w-5 text-amber-600" />
          <div>
            <h4 className="font-medium text-amber-900">Prep Notes</h4>
            <ul className="mt-2 space-y-1 text-sm text-amber-800">
              <li>• Verify all items against delivery receipt before starting prep</li>
              <li>• Check temperature logs for cold chain items</li>
              <li>• Report any quality issues to receiving manager immediately</li>
              <li>• Update inventory system after items are stored</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
